/**
 * 第三方账户：开放平台微信绑定 / 解绑
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertDialog,
  Button,
  Card,
  Modal,
  Separator,
  toast,
  Tooltip,
} from '@heroui/react';
import { IconCircleCheck, IconHelpCircle } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { handleTRPCError } from '@/utils/error-handler';
import { useWeChatBindQr } from '../hooks/useWeChatBindQr';

const TAB_THIRD_PARTY = 'third_party';

export function ThirdPartyAccountsSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bindModalOpen, setBindModalOpen] = useState(false);
  const [unbindDialogOpen, setUnbindDialogOpen] = useState(false);

  const bindReturnUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const url = new URL(window.location.href);
    url.searchParams.set('tab', TAB_THIRD_PARTY);
    url.hash = '';
    return url.toString();
  }, []);

  const { bootstrap, loadError } = useWeChatBindQr(bindModalOpen, bindReturnUrl);

  const bindingQuery = trpc.auth.getMyWechatBinding.useQuery(undefined, {
    retry: false,
  });

  const unbindMutation = trpc.auth.unbindWechat.useMutation({
    onSuccess: () => {
      setUnbindDialogOpen(false);
      toast.success('微信账号已解绑');
      void bindingQuery.refetch();
    },
    onError: (error) => {
      handleTRPCError(error);
    },
  });

  /** OAuth 绑定回跳 */
  useEffect(() => {
    const bindOk = searchParams.get('wechat_bind');
    const message = searchParams.get('message');

    if (bindOk === 'ok') {
      toast.success('微信账号绑定成功');
      void bindingQuery.refetch();
      const next = new URLSearchParams(searchParams);
      next.delete('wechat_bind');
      next.delete('message');
      next.delete('code');
      next.delete('errorType');
      setSearchParams(next, { replace: true });
      return;
    }

    if (message && searchParams.get('tab') === TAB_THIRD_PARTY) {
      toast.danger('微信绑定失败', { description: message });
      const next = new URLSearchParams(searchParams);
      next.delete('message');
      next.delete('code');
      next.delete('errorType');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, bindingQuery]);

  const handleBindModalOpenChange = useCallback((open: boolean) => {
    setBindModalOpen(open);
  }, []);

  const isBound = bindingQuery.data?.bound === true;
  const isLoading = bindingQuery.isLoading;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-6">
      <Card className="mx-auto w-full max-w-3xl">
        <Card.Content className="flex flex-col gap-0 p-4">
          <h2 className="px-1 py-2 text-base font-semibold text-foreground">微信</h2>
          <Separator />

          <div className="flex items-center justify-between gap-4 py-5">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {isBound ? (
                <>
                  <IconCircleCheck className="size-5 shrink-0 text-success" aria-hidden />
                  <span className="text-sm text-muted">已成功绑定微信账号</span>
                </>
              ) : (
                <>
                  <span className="text-sm text-muted">未绑定微信账号</span>
                  <Tooltip delay={0}>
                    <Tooltip.Trigger>
                      <button
                        type="button"
                        className="inline-flex text-muted hover:text-foreground"
                        aria-label="绑定说明"
                      >
                        <IconHelpCircle className="size-4" />
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Content placement="top">
                      绑定后可使用微信扫码登录人文派
                    </Tooltip.Content>
                  </Tooltip>
                </>
              )}
            </div>

            {isBound ? (
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0"
                onPress={() => setUnbindDialogOpen(true)}
                isDisabled={isLoading || unbindMutation.isPending}
              >
                解绑微信
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0"
                onPress={() => setBindModalOpen(true)}
                isDisabled={isLoading}
              >
                绑定微信
              </Button>
            )}
          </div>
        </Card.Content>
      </Card>

      <Modal isOpen={bindModalOpen} onOpenChange={handleBindModalOpenChange}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" size="sm">
            <Modal.Dialog>
              <Modal.Header>
                <Modal.Heading>绑定微信</Modal.Heading>
              </Modal.Header>
              <Modal.Body className="flex flex-col items-center gap-3 py-2">
                <p className="text-center text-sm text-muted">
                  请使用微信扫描下方二维码完成绑定
                </p>
                {loadError ? (
                  <p className="text-center text-sm text-danger">{loadError}</p>
                ) : null}
                {bootstrap && !bootstrap.configured ? (
                  <p className="text-center text-sm text-danger">{bootstrap.message}</p>
                ) : (
                  <div
                    id="wx_bind_container"
                    className="flex min-h-[200px] w-full items-center justify-center"
                  />
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onPress={() => setBindModalOpen(false)}>
                  取消
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      <AlertDialog isOpen={unbindDialogOpen} onOpenChange={setUnbindDialogOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>解绑微信</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要与该微信账号解除绑定关系吗？
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setUnbindDialogOpen(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={unbindMutation.isPending}
                  onPress={() => unbindMutation.mutate()}
                >
                  {unbindMutation.isPending ? '解绑中…' : '确定'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}

export { TAB_THIRD_PARTY };
